import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Download, Pencil, FileText } from 'lucide-react';
import {
  useFilterDocuments,
  useDeleteDocument,
  useUpdateDocumentStatus,
  useDraftDocumentsCount,
  useTotalDocumentsCount,
  useSearchDocuments
} from '@/lib/api/hooks/document';
import type { Document } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { ActionDropdown, TanStackTable, DeleteModal, PublishModal, UnpublishModal,  type ColumnDef, type DropdownAction, Pagination, Button } from '@/components/ui';
import ProcurementDocumentStats from './ProcurementDocumentStats';
import handleDownloadFromUrl from '@/utils/helpers/handleDownloadFromUrl';
import { Paragraph } from '@/components/ui/Typography';
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay';
import Search from '@/components/ui/Search';
import FilterModule from '@/components/ui/FilterModule';
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams';
import { useProcurementDocumentsFilterSections } from '@/pages/opportunities/procurement-docs/FilterModule/FILTER_SECTIONS';

type TabType = 'PUBLISH' | 'DRAFT';

type DocumentRow = Document & {
  createdByName?: string | null;
  publishedByName?: string | null;
  totalDownloads?: number;
  totalViews?: number;
  fileSize?: string | null;
};

const Documents: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('PUBLISH');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [unpublishModalOpen, setUnpublishModalOpen] = useState(false);
  const [selectedDocumentForDelete, setSelectedDocumentForDelete] = useState<Document | null>(null);
  const [selectedDocumentForStatusUpdate, setSelectedDocumentForStatusUpdate] = useState<Document | null>(null);

  const FILTER_SECTIONS = useProcurementDocumentsFilterSections();
                // ── URL-driven search / filter state ──────────────────────────────────────
                const {
                  mode,
                  searchTerm,
                  filterState,
                  page,
                  size,
                  setSearch,
                  setFilters,
                  removeFilter,
                  clearFilters,
                  setPage,
                  apiFilters,
                  getActiveFilterCount
                } = useSearchFilterParams(FILTER_SECTIONS)
              
              // Search API (when searching)
              const { data: searchData, isFetching: isSearchFetching, isError: isSearchError, refetch: refetchSearch } = useSearchDocuments(
              searchTerm,
                mode === 'search' 
              )
            
              // Filter API (when filtering or default)
              const { data: filterData, isFetching: isFilterFetching, isError: isFilterError, refetch: refetchFilter } = useFilterDocuments(
                { page, size },
                {   status: activeTab, ...apiFilters },
                mode === 'filter' || mode === 'default'
              )
            
              // Select appropriate data source: search takes priority
              const aggregateData = mode === 'search' ? searchData : filterData
              const isFetching = mode === 'search' ? isSearchFetching : isFilterFetching
              const isError = mode === 'search' ? isSearchError : isFilterError
            
              const totalPages = aggregateData?.data?.totalPage ?? 1
              const activeFilterCount = getActiveFilterCount()
              const refetchData = mode === 'search' ? refetchSearch : refetchFilter
              const emptyMessage = mode === 'search'
                ? 'No procurement documents found matching your search criteria.'
                : mode === "filter" ?  'No procurement documents found matching your filter criteria.'
                : 'No procurement documents found.'


  const { data: totalCountData } = useTotalDocumentsCount();
  const { data: draftCountData } = useDraftDocumentsCount();

  const deleteDocumentMutation = useDeleteDocument();
  const updateStatusMutation = useUpdateDocumentStatus();



  const filteredDocuments = aggregateData?.data?.data || [];

  const publishedCount = totalCountData?.data || 0;
  const draftCount = draftCountData?.data || 0;

  const handleAddDocument = () => {
    navigate('/admin/documents/add');
  };

  const handleViewDocument = (document: Document) => {
    navigate(`/admin/documents/${document.documentId}`);
  };

  const handleEditDocument = (document: Document) => {
    navigate(`/admin/documents/edit/${document.documentId}`, {
      state: { document }
    });
  };

  const handleDeleteDocument = (document: Document) => {
    setSelectedDocumentForDelete(document);
    setDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedDocumentForDelete) return;

    deleteDocumentMutation.mutate(
      { documentId: selectedDocumentForDelete.documentId },
      {
        onSuccess: (response) => {
          if (response.success) {
            showToast.success('Document deleted successfully');
            setDeleteModalOpen(false);
            setSelectedDocumentForDelete(null);
            refetchFilter();
          } else {
            showToast.error('Failed to delete document');
          }
        },
        onError: (error: Error) => {
          showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const handlePublishDocument = (document: Document) => {
    setSelectedDocumentForStatusUpdate(document);
    setPublishModalOpen(true);
  };

  const handleUnpublishDocument = (document: Document) => {
    setSelectedDocumentForStatusUpdate(document);
    setUnpublishModalOpen(true);
  };

  const confirmStatusUpdate = () => {
    if (!selectedDocumentForStatusUpdate) return;

    const newStatus = selectedDocumentForStatusUpdate.status === 'PUBLISH' ? 'DRAFT' : 'PUBLISH';

    updateStatusMutation.mutate(
      {
        documentId: selectedDocumentForStatusUpdate.documentId,
        data: { status: newStatus }
      },
      {
        onSuccess: () => {
          showToast.success(
            newStatus === 'PUBLISH'
              ? 'Document published successfully!'
              : 'Document unpublished successfully!'
          );
          setPublishModalOpen(false);
          setUnpublishModalOpen(false);
          setSelectedDocumentForStatusUpdate(null);
          refetchFilter();
        },
        onError: (error: Error) => {
          showToast.error('Failed to update status: ' + (error.message || 'Unknown error'));
        }
      }
    );
  };

  const getDropdownActions = (document: Document): DropdownAction[] => {
    const actions: DropdownAction[] = [
      {
        label: 'View',
        onClick: (e) => {
          e.stopPropagation();
          handleViewDocument(document);
        }
      },
      {
        label: 'Edit Document',
        onClick: (e) => {
          e.stopPropagation();
          handleEditDocument(document);
        }
      },
      {
        label: document.status === 'PUBLISH' ? 'Unpublish' : 'Publish',
        onClick: (e) => {
          e.stopPropagation();
          if (document.status === 'PUBLISH') {
            handleUnpublishDocument(document);
          } else {
            handlePublishDocument(document);
          }
        },
        disabled: updateStatusMutation.isPending
      }
    ];

    // Only show Delete for draft documents
    if (document.status === 'DRAFT') {
      actions.push({
        label: 'Delete',
        onClick: (e) => {
          e.stopPropagation();
          handleDeleteDocument(document);
        },
        variant: 'danger' as const,
        disabled: deleteDocumentMutation.isPending
      });
    }

    return actions;
  };

  const formatCategory = (category: string) => {
    return category
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getCategoryBadgeClass = (category: string) => {
    if (category.includes('POLICY')) return 'bg-violet-50 text-violet-700 border-violet-200';
    if (category.includes('GUIDELINE')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (category.includes('FORM')) return 'bg-amber-50 text-amber-700 border-amber-200';
    if (category.includes('TEMPLATE')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (category.includes('PRESENTATION')) return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  };

  const getFileType = (documentUrl?: string) => {
    if (!documentUrl) return 'FILE';
    const clean = documentUrl.split('?')[0].split('#')[0];
    const ext = clean.includes('.') ? clean.substring(clean.lastIndexOf('.') + 1).toUpperCase() : '';
    return ext || 'FILE';
  };

  const handleDownloadDocument = async (document: DocumentRow) => {
    if (!document.documentUrl) {
      showToast.error('No document URL available for download');
      return;
    };
    handleDownloadFromUrl({ url: document.documentUrl, fileName: document.title || 'document' });
  };

  const columns: ColumnDef<DocumentRow, unknown>[] = [
    {
      id: 'title',
      header: 'Document Title',
      cell: ({ row }) => {
        const doc = row.original;
        const fileType = getFileType(doc.documentUrl);

        const fileTypeConfig: Record<string, { bg: string; text: string }> = {
          PDF:  { bg: 'bg-red-100',     text: 'text-red-600'     },
          DOCX: { bg: 'bg-blue-100',    text: 'text-blue-600'    },
          DOC:  { bg: 'bg-blue-100',    text: 'text-blue-600'    },
          PPTX: { bg: 'bg-orange-100',  text: 'text-orange-600'  },
          PPT:  { bg: 'bg-orange-100',  text: 'text-orange-600'  },
          XLSX: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
          XLS:  { bg: 'bg-emerald-100', text: 'text-emerald-600' },
          CSV:  { bg: 'bg-emerald-100', text: 'text-emerald-600' },
          TXT:  { bg: 'bg-gray-100',    text: 'text-gray-600'    },
          FILE: { bg: 'bg-gray-100',    text: 'text-gray-600'    },
          JPG:  { bg: 'bg-purple-100',    text: 'text-purple-600'    },
          JPEG: { bg: 'bg-purple-100',    text: 'text-purple-600'    },
          PNG:  { bg: 'bg-purple-100',    text: 'text-purple-600'    },
          GIF:  { bg: 'bg-purple-100',    text: 'text-purple-600'    },
        };
        const { bg, text } = fileTypeConfig[fileType] ?? { bg: 'bg-gray-50', text: 'text-gray-500' };

        return (
          <div className="flex items-center gap-3 min-w-[320px]">
            <div className={`w-10 h-10 rounded-md border border-gray-200 ${bg} flex flex-col items-center justify-center shrink-0 gap-0.5`}>
              <FileText className={`w-5 h-5 ${text}`} />
            </div>
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-900 whitespace-normal break-words leading-tight">{doc.title}</p>
              <p className="text-sm text-gray-500 whitespace-normal break-words">{formatCategory(doc.documentCategory || '')}</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case', cellClassName: 'px-4 py-4 align-top whitespace-normal' },
    },
    {
      id: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const category = row.original.documentCategory || 'OTHER';
        return (
          <span className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-sm font-medium ${getCategoryBadgeClass(category)}`}>
            {formatCategory(category)}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const isPublished = row.original.status === 'PUBLISH';
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium ${isPublished ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            <span className={`h-2 w-2 rounded-full ${isPublished ? 'bg-emerald-500' : 'bg-amber-500'}`} />
            {isPublished ? 'Published' : 'Draft'}
          </span>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'uploadDate',
      header: 'Upload Date',
      cell: ({ row }) => (
        <span className="text-base text-gray-700">{formatDate(row.original.datePublished)}</span>
      ),
      meta: { headerClassName: 'px-4 py-4 !normal-case', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
    {
      id: 'uploadedBy',
      header: 'Uploaded By',
      cell: ({ row }) => {
        const doc = row.original;
        const name = doc.publishedByName || doc.createdByName || doc.username || 'Unknown';
        return (
          <div className="flex items-center gap-3 max-w-[220px]">
            <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-700 text-sm font-semibold flex items-center justify-center shrink-0">
              {getInitials(name)}
            </div>
            <div className="min-w-0">
              <p className="text-base text-gray-900 leading-tight truncate">{name}</p>
              <p className="text-sm text-gray-500">Admin</p>
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case', cellClassName: 'px-4 py-4' },
    },
    {
      id: 'fileType',
      header: 'File Type',
      cell: ({ row }) => {
        const doc = row.original;
        const fileType = getFileType(doc.documentUrl);
        return (
          <div>
            <p className="text-base text-gray-800 font-medium">{fileType}</p>
            {doc.fileSize && <p className="text-sm text-gray-500">{doc.fileSize}</p>}
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 text-center !normal-case', cellClassName: 'px-4 py-4 text-center whitespace-nowrap' },
    },
    {
      id: 'downloadsViews',
      header: 'Downloads / Views',
      cell: ({ row }) => {
        const doc = row.original;
        const totalDownloads = doc.totalDownloads ?? 0;
        const totalViews = doc.totalViews ?? 0;
        return <span className="text-base text-gray-700">{totalDownloads.toLocaleString()} / {totalViews.toLocaleString()}</span>;
      },
      meta: { headerClassName: 'px-4 py-4 text-center !normal-case', cellClassName: 'px-4 py-4 text-center whitespace-nowrap' },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const doc = row.original;
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              title="View"
              onClick={() => handleViewDocument(doc)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Eye className="w-5 h-5" />
            </button>
            <button
              type="button"
              title="Download"
              onClick={() => handleDownloadDocument(doc)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              type="button"
              title="Edit"
              onClick={() => handleEditDocument(doc)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-emerald-700 hover:bg-emerald-50"
            >
              <Pencil className="w-5 h-5" />
            </button>
            <div className="h-11 w-11 inline-flex items-center justify-center rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
              <ActionDropdown id={doc.documentId} actions={getDropdownActions(doc)} />
            </div>
          </div>
        );
      },
      meta: { headerClassName: 'px-4 py-4 !normal-case', cellClassName: 'px-4 py-4 whitespace-nowrap' },
    },
  ];

  const isEmptyState = !isFetching && mode === "default" && filteredDocuments.length === 0;

  return (
    <div className="space-y-6">
      <ProcurementDocumentStats />

      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-0">
          <button
            onClick={() => {
              setActiveTab('PUBLISH');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'PUBLISH'
                ? 'bg-wine-100 text-white'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Published ({publishedCount})
          </button>
          <button
            onClick={() => {
              setActiveTab('DRAFT');
              setPage(1);
            }}
            className={`px-6 py-2.5 text-sm font-semibold transition-colors rounded-md ${activeTab === 'DRAFT'
                ? 'bg-wine-100 text-white'
                : 'text-gray-700 hover:bg-gray-100'
              }`}
          >
            Drafts ({draftCount})
          </button>
        </div>
        <button
          onClick={handleAddDocument}
          className="bg-[#047857] hover:bg-[#059669] text-white font-semibold py-2.5 px-6 rounded-md text-sm"
        >
          Add Document
        </button>
      </div>

      {/* Search and Table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
  {/* Search + Filter Controls */}
                          <div className="flex flex-col gap-4 p-4 border border-gray-200 rounded-md bg-white shadow-sm">
                            <div className="flex items-center relative gap-4 justify-between flex-wrap">
                              <Search
                                value={searchTerm}
                                onSearch={setSearch}
                                placeholder="Search"
                              />
                               <FilterModule
                                    sections={FILTER_SECTIONS}
                                    activeFilters={filterState}
                                    onApply={setFilters}
                                    activeFilterCount={activeFilterCount}
                                  />
                            </div>
                
                            {/* Active filter chips — only shown in filter mode */}
                            <FilterParamsDisplay
                              sections={FILTER_SECTIONS}
                              filterState={filterState}
                              onRemove={removeFilter}
                              onClearAll={clearFilters}
                            />
                          </div>

        {isFetching ? <TanStackTable data={[]} columns={columns} loading /> :
              isError ? (
          <div className="p-8 flex items-center justify-center  flex-col gap-4 text-center">
            <Paragraph className="">Error loading data.</Paragraph>
            <Button
              onClick={() => {
                refetchData();
              }}
              className=""
            >
              Retry
            </Button>
          </div>
        ) : isEmptyState ? (
                <div className="text-center py-20">
                  <div className="inline-block bg-gray-100 p-4 rounded-full mb-4">
                    <img src="/No-post-icon.png" alt="No posts" className="w-10 h-10 object-contain" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-lg">
                    {activeTab === 'PUBLISH' ? 'No Recent Procurement Documents' : 'No Draft'}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {activeTab === 'PUBLISH' ? 'You are yet to create a procurement document' : 'Your draft is empty'}
                  </p>
                    <Button variant="primary" className="mt-4"  onClick={handleAddDocument}>
                      Add Document
                    </Button>
                </div>
              ) : (
          <>
          <div className="overflow-x-auto">
            <TanStackTable<DocumentRow>
              data={filteredDocuments as DocumentRow[]}
              columns={columns}
              getRowId={(row) => row.documentId}
              onRowClick={(row) => handleViewDocument(row)}
              tableClassName="min-w-[1400px] w-full divide-y divide-gray-200"
              theadClassName="bg-[#FAFAFA]"
              tbodyClassName="bg-white divide-y divide-gray-200"
              rowClassName="hover:bg-gray-50 transition-colors"
              emptyMessage={emptyMessage}
              emptyClassName="px-6 py-12 text-center text-sm text-gray-500"
            />
          </div>
                <Pagination
                                   currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                                className="border-t border-gray-200 p-4"
                              />
          </>
        )}
      </div>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Document"
        subtitle="Are you sure you want to delete this document?"
        itemName={selectedDocumentForDelete?.title}
        warningText="This action cannot be undone. The document will be permanently deleted."
        isLoading={deleteDocumentMutation.isPending}
      />

      {/* Publish Modal */}
      {selectedDocumentForStatusUpdate && selectedDocumentForStatusUpdate.status === 'DRAFT' && (
        <PublishModal
          isOpen={publishModalOpen}
          onClose={() => setPublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          title="Publish Document?"
          subtitle="Are you sure you want to publish this document?"
          itemName={selectedDocumentForStatusUpdate.title}
          infoText="This document will be visible to the public once published."
          itemType="Document"
          isLoading={updateStatusMutation.isPending}
        />
      )}

      {/* Unpublish Modal */}
      {selectedDocumentForStatusUpdate && selectedDocumentForStatusUpdate.status === 'PUBLISH' && (
        <UnpublishModal
          isOpen={unpublishModalOpen}
          onClose={() => setUnpublishModalOpen(false)}
          onConfirm={confirmStatusUpdate}
          itemType="Document"
          isLoading={updateStatusMutation.isPending}
        />
      )}
    </div>
  );
};

export default Documents;
